import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { get } from 'lodash';
import {
  Table,
  Tag,
  Button,
  Row,
  Select,
  Space,
  Form,
  Input,
  Col,
  notification,
  Empty,
  Typography,
  Popconfirm,
  message,
  Tooltip,
  Dropdown,
} from "antd";
import styled from "styled-components";
import DropdownActions from "../DropdownActions/DropdownActions";
import {
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import FontAwesomeIcon from "components/Common/FontAwesomeIcon";
import { ThemeConfig } from "theme";
import { TableDarkHeader } from "components/Common/TableStyles";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  EJEPSON_COLUMNS,
  ENTERPRISE_COLUMNS_HEADERS,
  HiddenColumnKeys,
  // NameColumn,
  NameInnerColumnKeys,
  PLANT_COMMON_COLUMNS,
  PLANT_COMMON_COLUMNS_HEADERS,
} from "./ejepson_table";
import { Resizable } from "react-resizable";
import {
  WILDLIFE_COLUMNS,
  WILDLIFE_COMMON_COLUMNS,
  WILDLIFE_COMMON_COLUMNS_HEADERS,
} from "./wild_life_table";
import { refresh, titleCase, truncateWithEllipsis } from "helper";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import SelectColumn from "./SelectColumn";
import CategoryDropdowns from "./CategoryDropdowns";
import { LoadingSpinnerView } from "components/LoadingSpinner/LoadingSpinner";
import Api from "api";
import CollectionErrorReviewModal from "components/Models/CollectionErrorReviewModal";
import UpdateReportModal from "components/Models/UpdateReportModal";
import {
  addCollectionItem,
  addItemNote,
  deleteCollection,
  deleteCollectionItem,
} from "api/helper";
import { useHistory } from "react-router-dom";
import {
  animalDatabases,
  arrangeByOptions,
  extraDefaultColumns,
  hideColumns,
  searchOptions,
} from "./constants";
import SearchInput from "components/Common/SearchInput";
import CloneCollectionModal from "components/Models/CloneCollectionModal";
import ErrorResult from "components/Common/ErrorResult";
import {
  usePlantFinderTiers,
  buildTierColumn,
  PlantFinderContextBanner,
  TIER_COLUMN_KEY,
} from "./PlantFinderTiers";
import ItemNote from "./ItemNote";
import config from "config";
import { useUser } from "helper/UserContext";
import CollectionDownloadModal from "components/Models/CollectionDownloadModal";
import { getTableScroll } from "helper/tableUtils";
import TaxonomicChangesPopup from "components/TaxonomicChanges/TaxonomicChangesPopup";
import TaxonomicChangesDrawer from "components/TaxonomicChanges/TaxonomicChangesDrawer";
import TaxonomicChangeBadge from "components/TaxonomicChanges/TaxonomicChangeBadge";
import SurveyAreaDrawer from "components/SurveyArea/SurveyAreaDrawer";
import SurveyAreaBadge from "components/SurveyArea/SurveyAreaBadge";
import SurveyAreaMiniMap from "components/SurveyArea/SurveyAreaMiniMap";
import RangeStatusCell from "components/SurveyArea/RangeStatusCell";
import { buildCspRegionColumn } from "./CspRegions";
import { isOutOfRange } from "utils/jepsonRegions";
import { JEPSON_DATA_ENABLED, filterJepsonColumns } from "utils/jepsonVisibility";
const { Paragraph } = Typography;

interface Props {
  type?: string;
}

/**
 * Local error boundary for <SurveyAreaMiniMap>. Defense-in-depth behind the
 * isValidGeometry guard in SurveyAreaMiniMap itself: if Leaflet ever throws
 * for an unforeseen reason (a future GeoJSON shape that slips past the
 * validator, a projection error, etc.), this boundary contains the failure
 * to the 80x32 minimap slot instead of letting it propagate to App.tsx's
 * top-level boundary and replace the whole app with "Something went wrong".
 */
class SurveyAreaMiniMapBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error("SurveyAreaMiniMap failed:", error);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const { Option } = Select;
const CollectionItemsTableWrapper = styled.div`
  .ant-table-cell {
    text-align: left;
  }
  .collectionColumnButton .ant-select-selection-item {
    display: none !important;
  }
  .collectionColumnButton .ant-select-selector::before {
    position: absolute;
    content: "Columns";
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: left;
    padding-left: 10px;
    align-items: center;
  }
  .collectionColumnButton .ant-select-selector {
    background: ${ThemeConfig.colors.skyBlue};
    color: #fff;
  }
  .ant-table-thead > tr > th {
    padding: 3px 5px !important;
    font-size: 12px;
  }
  .ant-table-tbody > tr > td {
    padding: 4px 8px !important;
    font-size: 12px;
    line-height: 1.3;
  }
  .ant-table-tbody > tr.taxonomic-change-pending-row > td {
    background-color: #fffbe6 !important;
  }
  .ant-table-tbody > tr.taxonomic-change-pending-row:hover > td {
    background-color: #fff1b8 !important;
  }
  .ant-table-tbody > tr.out-of-range-row > td {
    background-color: #fff7e6 !important;
  }
  .ant-table-tbody > tr.out-of-range-row:hover > td {
    background-color: #ffe7ba !important;
  }
  .ant-table-tbody > tr.missing-from-catalog-row > td {
    background-color: #fafafa !important;
    color: rgba(0, 0, 0, 0.55);
  }
`;

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;
  // console.log('props are', props, width);
  if (!width) {
    // console.log("yoyo are", props);
    // return null
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      // style={{maxWidth:}}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};
const emptyRows = [[], [], [], [], [], [], [], []];
const alphaSorter = (a, b) => {
  if (a === b) return 0;

  // Handle sp. sorting - "sp." should appear first within each genus
  const aIsSpecies = a && typeof a === 'string' && a.includes(' sp.');
  const bIsSpecies = b && typeof b === 'string' && b.includes(' sp.');

  if (aIsSpecies && bIsSpecies) {
    // Both are "sp." - sort normally
    return a < b ? -1 : 1;
  }

  if (aIsSpecies || bIsSpecies) {
    // Extract genus names for comparison
    const aGenus = aIsSpecies ? a.split(' sp.')[0] : (a && typeof a === 'string' ? a.split(' ')[0] : '');
    const bGenus = bIsSpecies ? b.split(' sp.')[0] : (b && typeof b === 'string' ? b.split(' ')[0] : '');

    if (aGenus === bGenus) {
      // Same genus - "sp." should come first
      return aIsSpecies ? -1 : 1;
    }
  }

  // Default alphabetical sort
  return a < b ? -1 : 1;
};
const CollectionItemsTable = ({}: Props) => {
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const [updateReportModalOpen, setUpdateReportModalOpen] = useState<boolean>(false);
  const [taxPopupOpen, setTaxPopupOpen] = useState<boolean>(false);
  const [taxDrawerOpen, setTaxDrawerOpen] = useState<boolean>(false);
  const [surveyAreaDrawerOpen, setSurveyAreaDrawerOpen] = useState<boolean>(false);
  const [pendingTaxChangesCount, setPendingTaxChangesCount] = useState<number>(0);
  const [defaultSort, setDefaultSort] = useState<any>(null);
  const { user } = useUser();
  const [tableSettings, setTableSettings] = useState<any>({
    inputPlantIdName: "",
    term: "",
    showSelectColumns: false,
    isToggleOn: true,
    defaultSorted: [
      {
        id: "family_name",
        desc: false,
      },
    ],
  });
  const { collectionId } = useParams();
  // Likelihood tiers when this collection was sent from CSP's Plant Finder;
  // null for every ordinary collection.
  const plantFinder = usePlantFinderTiers(collectionId);
  const [arrangeBy, setArrangeBy] = useState<any>("family");
  const [database, setDatabase] = useState<any>("all");
  const [searchBy, setSearchBy] = useState<any>("all");
  const [excludeIds, setExcludeIds] = useState<any>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [autoCompleteItems, setAutoCompleteItems] = useState<any>([]);
  const [tableData, setTableData] = useState<any>(emptyRows);
  const [items, setItems] = useState<any>([]);
  const [columns, setColumns] = useState<any>([]);
  const [collection, setCollection] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const history = useHistory();
  
  const location = useLocation();

  const matchedRegions: string[] = collection?.matched_regions || [];
  // Survey Area matches a collection against Jepson bioregions, so the whole
  // feature — the button, the map, the Range Status column, the out-of-range
  // row highlighting — is off while Jepson data is hidden. matched_regions stays
  // on the collection untouched; it is just not displayed. Re-pointing this at
  // observed_bioregions (GBIF) is a planned follow-up.
  const hasSurveyArea = JEPSON_DATA_ENABLED && matchedRegions.length > 0;

  const outOfRangeCount = useMemo(() => {
    if (!hasSurveyArea || !items || items.length === 0) return 0;
    return items.filter((item: any) =>
      isOutOfRange(item.bioregional_distributions, matchedRegions)
    ).length;
  }, [items, matchedRegions, hasSurveyArea]);

  const handleDeleteCollectionItem = async (id) => {
    setTableLoading(true);
    //  console.log('deleting', id);
    //  return;
    const delRes = await deleteCollectionItem(collection.id, id, {
      type: collection.type,
    });
    setTableLoading(false);
    if (delRes && delRes.error) {
      notification.error({
        message: "Delete Failed",
        description: delRes.error,
      });
      // setLoading(false);
    } else {
      notification.success({
        message: "Deleted",
        description: delRes?.data?.messages[0] || "Success",
      });
      //  refetch && refetch();
      fetchCollection();
      //  window.location.href = "/collections";
      // refresh();
      // window.location.reload();
      // history.push("/collections");
    }
  };
  const initializeColumns = () => {
    if(columns && columns.length) {
      return;
    }
    // filterJepsonColumns is belt-and-braces: the API already omits these from
    // column_names while the flag is off, so this only matters if a stale
    // response or a cached payload still carries them.
    const filteredColumns = filterJepsonColumns(collection.column_names)
      .filter((s) => ![...NameInnerColumnKeys].includes(s.name) && ![...HiddenColumnKeys].includes(s.name))
      .map((col) => {
        return {
          title: col.export_name,
          dataIndex: col.name,
          sorter: true,
          show: !hideColumns.includes(col.name),
        };
      });
      var _columns: any = [];
      _columns = [
        filteredColumns[0],
        filteredColumns[1],
        {
          title: "Name",
          sorter: false,
          show: true,
          children: [
            {
              title: "Family",
              headerClassName: "family-header",
              dataIndex: "family",
              key: "family",
              className: "centered capitalize",
              // sorter: true,
              width: 100,
              sorter: alphaSorter,
              sortOrder:
                defaultSort &&
                defaultSort.columnKey === "family" &&
                defaultSort.order,
              ellipsis: true,
              show: true,
            },
            {
              title: "Genus",
              headerClassName: "genus-header",
              dataIndex: "genus",
              key: "genus",
              className: "centered capitalize",
              // Dangling reference (see collections.js show): the stored name in full.
              // textTransform overrides the cell's "capitalize" class, which would
              // otherwise capitalize the epithet ("Cryptantha Micrantha").
              render: (value: any, record: any) => record && record.missing_from_catalog ? (<span className="missing-from-catalog-name" style={{ textTransform: "none" }} title="This plant was reclassified in an earlier update; its record is being restored.">{record.full_name || value || "Unknown species"} <em style={{ opacity: 0.7 }}>— no longer in catalog, being updated</em></span>) : value,
              width: 100,
              sorter: alphaSorter,
              sortOrder:
                defaultSort &&
                defaultSort.columnKey === "genus" &&
                defaultSort.order,
              ellipsis: true,
              show: true,
            },
            {
              title: "species",
              dataIndex: "species",
              key: "species",
              className: "centered",
              width: 100,
              sorter: alphaSorter,
              sortOrder:
                defaultSort &&
                defaultSort.columnKey === "species" &&
                defaultSort.order,
              ellipsis: true,
              show: true,
            },
            {
              title: "Variety/Subspecies",
              dataIndex: "variety",
              key: "variety",
              className: "centered",
              width: 200,
              sorter: alphaSorter,
              sortOrder:
                defaultSort &&
                defaultSort.columnKey === "variety" &&
                defaultSort.order,
              ellipsis: true,
              show: true,
            },
          ],
        },
        ...filteredColumns.slice(2),
        // CSP regions (GBIF-recorded + could-occur). Served on every plant row
        // outside column_names; the API trims "could occur" while Jepson is hidden.
        ...(collection.type === "plant" ? [buildCspRegionColumn()] : []),
        ...(hasSurveyArea
          ? [
              {
                title: "Range Status",
                dataIndex: "bioregional_distributions",
                key: "range_status",
                width: 120,
                show: true,
                className: "centered",
                render: (bioregDist: any, record: any) => (
                  <RangeStatusCell
                    bioregionalDistributions={record.bioregional_distributions}
                    matchedRegions={matchedRegions}
                  />
                ),
              },
            ]
          : []),
        {
          title: "Note",
          // sorter: true,
          width: 200,
          show: true,
          dataIndex: "note",
          render: (a, r) => {
            return (
              <ItemNote
                itemId={r.item_id}
                note={a}
                type={collection.type}
                collectionId={collection.id}
                setTableLoading={setTableLoading}
              />
            );
          },
        },
        {
          title: "Actions",
          dataIndex: "actions",
          show: true,
          className: "centered",
          render: (a, r) => {
            if (!r.item_id) {
              return;
            }
            // <span className="label label-danger link" onClick={() => {}}>
            //   <i className="fa fa-trash"></i> Delete
            // </span>
            // <Popconfirm
            //   placement="top"
            //   title={"Are you sure you wish to delete this collection?"}
            //   onConfirm={() => handleDeleteCollection(record.id)}
            //   okText="Yes"
            //   cancelText="No"
            // >
            return (
              <Button
                type="primary"
                icon={<DeleteOutlined />}
                size="small"
                onClick={(record: any) => handleDeleteCollectionItem(r.item_id)}
              >
                Delete
              </Button>
            );
            // </Popconfirm>
          },
        },
      ];
      setColumns(
        _columns.map((s) => ({
          ...s,
          ...(s.width ? { width : s.width} : {}),
          // width: s.width ? s.width : 400,
          // ellipsis: true,
          onCell: () => {
            return {
              style: {
                whiteSpace: 'nowrap',
                ...(s.width ? { maxWidth : s.width} : {}),
                minWidth: 150,
                // maxWidth: s.width ? s.width : 150,
              }
            }
          },         
        }))
      );
  }

  const initializeData = () => {
    initializeColumns();
    setTableData(
      collection.items && collection.items.length > 0
        ? [...collection.items]
        : emptyRows
    );
    setItems(
      collection.items && collection.items.length > 0 ? collection.items : []
    );
    setExcludeIds(collection.items.map((s) => s.original_id));
    // setAutoCompleteItems([]);
    setLoading(false);
  };
  const fetchCollection = async () => {
    setTableLoading(true);
    try {
      const { data } = await Api.get(`/collections/${collectionId}`, {});
      if(data.data && data.data.is_user_upload && !data.data.mapped) {
        notification.warning({
          message: 'Action required',
          description: "You have not completed mapping for upload collection."
        })
        history.push(`/collections/${data.data.id}/save_mapped_columns`);
      } else if (data) {
        setCollection(data);
        if (!data.error_fixed) {
          setErrorModalOpen(true);
        }
        // Check for pending taxonomic changes
        const taxCount = data.pending_taxonomic_changes_count || 0;
        setPendingTaxChangesCount(taxCount);
        if (taxCount > 0) {
          const sessionKey = `tax_popup_shown_${data.id}`;
          if (!sessionStorage.getItem(sessionKey)) {
            setTaxPopupOpen(true);
            sessionStorage.setItem(sessionKey, "true");
          }
        }
      }
    } catch (e: any) {
      console.log("herereee", e);
      const errorMsg =
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.messages &&
          e.response.data.messages[0]) ||
        "Failed to Fetch Colletion";
      console.log(errorMsg);
      message.error({ content: errorMsg, duration: 3 });
    }
    setTableLoading(false);
    setLoading(false);
  };
  useEffect(() => {
    setColumns([]);
    fetchCollection();
  }, [collectionId]);
  useEffect(() => {
    if (collection) {
      initializeData();
    }
    // setLoading(false);
  }, [collection]);
  const handleResize =
    (index) =>
    (e, { size }) => {
      const nextColumns = [...columns];
      nextColumns[index] = {
        ...nextColumns[index],
        width: size.width > 100 ? size.width : 100,
      };
      // check if children //
      if(nextColumns[index].children){
        const diffWidth = size.width - columns[index].width;
        const diffPerChildWidth = Math.floor(diffWidth / nextColumns[index].children.length);
        const childHeaders = [...nextColumns[index].children]
        for (let k = 0; k < childHeaders.length; k++) {
          childHeaders[k].width = childHeaders[k].width + diffPerChildWidth
        }
        nextColumns[index] = {
          ...nextColumns[index],
          width: size.width > 100 ? size.width : 100,
          children: [...childHeaders],
        };
      }
      // end of check children //
      setColumns(nextColumns);
    };
  const handleColumnsChange = (values) => {
    const modifiedColumns = [...columns].map((s, i) => ({
      ...s,
      show: values.find((x) => x.value === i),
    }));
    setColumns(modifiedColumns);
  };
  const finalColumns = columns
    .map((col, index) => {
      const _extras:any = {};
      if(col.dataIndex === 'synonyms') {
        _extras.render = (tt) => <Tooltip title={tt}>{truncateWithEllipsis(tt, 60)}</Tooltip>;
      }

      // Check if this column should show sort order (including child columns)
      let sortOrder = null;
      if (defaultSort) {
        if (col.dataIndex === defaultSort.columnKey) {
          sortOrder = defaultSort.order;
        } else if (col.children) {
          // Check child columns for sort order
          const childMatch = col.children.find(child => child.key === defaultSort.columnKey || child.dataIndex === defaultSort.columnKey);
          if (childMatch) {
            // Apply sort order to the matching child column
            col.children = col.children.map(child => ({
              ...child,
              sortOrder: (child.key === defaultSort.columnKey || child.dataIndex === defaultSort.columnKey) ? defaultSort.order : null
            }));
          }
        }
      }

      return {
        ...col,
        sorter:
          col.sorter &&
          (typeof col.sorter === "function" ? col.sorter : alphaSorter),
        ..._extras,
        sortOrder,
        onHeaderCell: (column) => ({
          width: column.width,
          onResize: handleResize(index),
        }),
      };
    })
    .filter((s) => s.show);
  // Injected at render rather than into `columns` state, so the column picker,
  // resizing and one-time column initialisation are all left untouched.
  const tableColumns = plantFinder
    ? [
        ...finalColumns.slice(0, 2),
        buildTierColumn(
          plantFinder,
          defaultSort && defaultSort.columnKey === TIER_COLUMN_KEY ? defaultSort.order : null
        ),
        ...finalColumns.slice(2),
      ]
    : finalColumns;
  const screens = useBreakpoint();
  const onCollectionNameChange = async (collectionName) => {
    setLoading(true);
    // return;
    try {
      const { data } = await Api.put(`/collections/${collection.id}`, {
        name: collectionName,
      });
      notification.success({
        message: "Collection Renamed",
        description: "d",
      });
      fetchCollection();
    } catch (e: any) {
      console.log("error", e.response.data.messages[0]);
      setLoading(false);
      const errorMsg =
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.messages &&
          e.response.data.messages[0]) ||
        "Failed to map columns";
      notification.error({
        message: "Failed",
        description: errorMsg,
      });
    }
  };
  const handleDeleteCollection = async () => {
    const delRes = await deleteCollection(collection.id);
    if (delRes && delRes.error) {
      notification.error({
        message: "Delete Failed",
        description: delRes.error,
      });
    } else {
      notification.success({
        message: "Deleted",
        description: delRes.data || "Success",
      });
      window.location.href = '/collections';
      // history.push("/collections");
    }
  };
  const handleItemAdd = async (record_id) => {
    const { data, error } = await addCollectionItem(collection.id, {
      record_id,
      type: collection.type,
    });
    // TODO: show error here maybe
    fetchCollection();
  };
  const handleArrangeByChange = (v) => {
    const newSort = {
      order: "ascend",
      columnKey: v,
    };
    setDefaultSort(newSort);
    setArrangeBy(v);

    // Force table re-sort by triggering the table data to re-render
    // This ensures the table picks up the new sort order
    const sortedData = [...tableData].sort((a, b) => {
      const aValue = a[v];
      const bValue = b[v];
      return alphaSorter(aValue, bValue);
    });
    setTableData(sortedData);
  };
  const handleExportToCSP = async (guideType: 'field_guide' | 'target_species_list') => {
    try {
      const label = guideType === 'target_species_list' ? 'target species list' : 'field guide';
      message.loading({ content: `Preparing ${label}...`, key: 'csp-export' });
      const res = await Api.post('/export/to-csp', {
        collection_id: collection.id,
        guide_type: guideType,
      });
      if (res.data?.success && res.data?.redirect_url) {
        message.success({
          content: `${res.data.species_count} species ready. Opening California Species...`,
          key: 'csp-export',
        });
        window.open(res.data.redirect_url, '_blank', 'noopener,noreferrer');
      } else {
        message.error({ content: 'Export failed', key: 'csp-export' });
      }
    } catch (e: any) {
      message.error({ content: 'Failed to export to CA Species', key: 'csp-export' });
    }
  };

  const downloadFile = async () => {
    try {
      if (user && !(user.can_export ?? (user.status !== "free")) && !user.one_time_purchase) {
        setDownloadModalOpen(true);
        return;
      }
      // return;
      const res = await Api.get(
        `/collections/${collection.id}/download?type=xls`,
        {},
        { responseType: "blob" }
      );
      if (res && res.data) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${collection.name || "collection"}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      } else {
        notification.error({
          message: "Download failed",
          description: "Failed to download excel",
        });
      }
    } catch(e: any) {
      notification.error({
        message: "Download failed",
        description:  "Failed to download file",
      });
    }
  };
  useEffect(() => {
    if(collection) {
      initializeColumns();
    }
  }, [defaultSort])
  
  // console.log(screens);
  if (loading) {
    return <LoadingSpinnerView size={80} />;
  }
  if (!collection) {
    return <ErrorResult />;
  }
  // if(!collection) {
  //   return <Empty />;
  // }
  return (
    <CollectionItemsTableWrapper>
      {/* <TableDarkHeader>{collection.type}</TableDarkHeader> */}
      {/* <TableDarkHeader>
        {type === "plant" ? "Plant" : "Wildlife"}
      </TableDarkHeader> */}
      <Row>
        <Paragraph
          editable={{
            onChange: onCollectionNameChange,
            tooltip: "click to edit text",
            triggerType: ["text", "icon"],
          }}
        >
          {titleCase(collection.name)}
        </Paragraph>
      </Row>
      <Row style={{ paddingBottom: 10 }}>
        <Col lg={{ span: 5 }} span={24}>
          <label>&nbsp;</label>
          {/* <Input.Search></Input.Search> */}
          <SearchInput
            type={collection.type}
            collectionId={collection.id}
            placeholder="Search..."
            onSelect={handleItemAdd}
            searchKey={searchBy}
            database={database}
            excludeIds={excludeIds}
          />
        </Col>
        <Col lg={10} span={24}>
          <Row>
            <Col xs={8}>
              <label>Search By</label>
              <Select
                dropdownMatchSelectWidth={false}
                defaultValue={searchBy}
                onChange={(v) => {
                  setSearchBy(v);
                }}
                style={{ width: "100%" }}
                // onChange={handleChange}
              >
                {searchOptions.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={8}>
              <label>Arrange By</label>
              <Select
                dropdownMatchSelectWidth={false}
                value={arrangeBy}
                onChange={handleArrangeByChange}
                style={{ width: "100%" }}
                // style={{ width: 150 }}
                // onChange={handleChange}
              >
                {arrangeByOptions.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            </Col>
            {collection.type === "animal" && (
              <Col xs={8}>
                <label>Database</label>
                <Select
                  dropdownMatchSelectWidth={false}
                  defaultValue={database}
                  onChange={(v) => {
                    setDatabase(v);
                  }}
                  style={{ width: "100%" }}
                  // style={{ width: 150 }}
                  // onChange={handleChange}
                >
                  {animalDatabases.map((s) => (
                    <Option key={s.value} value={s.value}>
                      {s.label}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
          </Row>
        </Col>
        <Col lg={9} xs={24}>
          <Row style={{ justifyContent: "flex-end" }}>
            <Col>
              <label style={{ display: "flex" }}>&nbsp;</label>
              <Button onClick={downloadFile}>Download Excel File</Button>
            </Col>
            <Col>
              <label style={{ display: "flex" }}>&nbsp;</label>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'target',
                      label: 'Target Species List',
                      onClick: () => handleExportToCSP('target_species_list'),
                    },
                    {
                      key: 'guide',
                      label: 'Field Guide',
                      onClick: () => handleExportToCSP('field_guide'),
                    },
                  ],
                }}
              >
                <Button
                  icon={<EnvironmentOutlined />}
                  style={{ borderColor: '#52c41a', color: '#52c41a' }}
                >
                  Make Guide <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
            {collection?.type === "plant" && JEPSON_DATA_ENABLED && (
              <Col>
                <label style={{ display: "flex" }}>&nbsp;</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Button
                    onClick={() => setSurveyAreaDrawerOpen(true)}
                    icon={<EnvironmentOutlined />}
                    style={
                      hasSurveyArea
                        ? { borderColor: "#52c41a", color: "#52c41a" }
                        : {}
                    }
                  >
                    Survey Area
                    <SurveyAreaBadge count={outOfRangeCount} />
                  </Button>
                  <SurveyAreaMiniMapBoundary>
                    <SurveyAreaMiniMap
                      surveyArea={collection?.survey_area}
                      onClick={() => setSurveyAreaDrawerOpen(true)}
                    />
                  </SurveyAreaMiniMapBoundary>
                </div>
              </Col>
            )}
            {pendingTaxChangesCount > 0 && (
              <Col>
                <label style={{ display: "flex" }}>&nbsp;</label>
                <Button
                  onClick={() => setTaxDrawerOpen(true)}
                  style={{ borderColor: "#faad14", color: "#faad14" }}
                >
                  Taxonomic Changes
                  <TaxonomicChangeBadge count={pendingTaxChangesCount} />
                </Button>
              </Col>
            )}
            {collection?.document && (
              <Col>
                <label style={{ display: "flex" }}>&nbsp;</label>
                <Button onClick={() => setUpdateReportModalOpen(true)}>
                  Updates Made from Original List
                </Button>
              </Col>
            )}
            {!collection.error_fixed && !!collection.error_count && (
              <Col>
                <label style={{ display: "flex" }}>&nbsp;</label>
                <Button type="primary" onClick={() => setErrorModalOpen(true)}>
                  Continue Matching
                </Button>
                <CollectionErrorReviewModal
                  isOpen={errorModalOpen}
                  onClose={(action) => {
                    if (action === "refetch") {
                      fetchCollection();
                    }
                    setErrorModalOpen(false);
                  }}
                  collection={collection}
                />
              </Col>
            )}
            {downloadModalOpen && (
              <CollectionDownloadModal
                isOpen={downloadModalOpen}
                onClose={(action) => {
                  //   if (action === "refetch") {
                  //     fetchCollection();
                  //   }
                  //   setErrorModalOpen(false);
                  setDownloadModalOpen(false);
                }}
                collectionId={collection.id}
                // collection={collection}
              />
            )}
            {updateReportModalOpen && (
              <UpdateReportModal
                isOpen={updateReportModalOpen}
                onClose={() => setUpdateReportModalOpen(false)}
                collectionId={collection.id}
                collectionName={collection.name}
              />
            )}
            <Col>
              <label style={{ display: "flex" }}>&nbsp;</label>
              <Popconfirm
                placement="topLeft"
                title={"Are you sure you wish to delete this collection?"}
                onConfirm={handleDeleteCollection}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="primary"
                  style={{ backgroundColor: "red" }}
                  icon={<DeleteOutlined />}
                >
                  {!screens.xs ? "Delete" : ""}
                </Button>
              </Popconfirm>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <CategoryDropdowns
            onChange={handleColumnsChange}
            columns={columns}
            type={collection?.type}
          />
        </Col>
      </Row>
      {plantFinder && <PlantFinderContextBanner data={plantFinder} />}
      <Table
        rowClassName={(record) => {
          if (record.has_pending_taxonomic_change) return "taxonomic-change-pending-row";
          if (record.missing_from_catalog) return "missing-from-catalog-row";
          if (hasSurveyArea && isOutOfRange(record.bioregional_distributions, matchedRegions)) return "out-of-range-row";
          return "";
        }}
        onChange={(pgn, flt, srt: any, act: any) => {
          if (act && act.action === "sort") {
            if (srt && srt.field) {
              // Update arrange by for specific fields
              if (["genus", "family", "created_at"].includes(srt.field)) {
                setArrangeBy(srt.order ? srt.field : null);
              }
              // Set the default sort with proper structure
              setDefaultSort({
                order: srt.order,
                columnKey: srt.field
              });
            } else {
              // Clear sorting
              setDefaultSort(null);
            }
          }
        }}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        // scroll={{ x: true }}
        scroll={{ x: "max-content", y: getTableScroll({ extraHeight: 200, id: null }) }}
        loading={loading || tableLoading}
        dataSource={tableData}
        // onChange={handleTableChange}
        //   pagination={{
        //     pageSize: pageLimit,
        //     total: pagination.total,
        //     showTotal: (total, range) =>
        //       `${range[0]}-${range[1]} of ${total} items`,
        //     // onChange: handlePageChange,
        //   }}
        pagination={{
          position: ["bottomCenter"],
          pageSize: 1000, // Show all species by default
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200', '500', '1000'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} species`,
        }}
        // style={{ minHeight: 800 }}
        rowKey={(record, i) => record.item_id || record.id || `t${i}`}
        columns={tableColumns}
      />
      {taxPopupOpen && (
        <TaxonomicChangesPopup
          isOpen={taxPopupOpen}
          onClose={(action) => {
            if (action === "refetch") {
              fetchCollection();
            }
            setTaxPopupOpen(false);
          }}
          collectionId={collection.id}
        />
      )}
      {taxDrawerOpen && (
        <TaxonomicChangesDrawer
          isOpen={taxDrawerOpen}
          onClose={(action) => {
            if (action === "refetch") {
              fetchCollection();
            }
            setTaxDrawerOpen(false);
          }}
          collectionId={collection.id}
        />
      )}
      {surveyAreaDrawerOpen && JEPSON_DATA_ENABLED && (
        <SurveyAreaDrawer
          isOpen={surveyAreaDrawerOpen}
          onClose={(action) => {
            if (action === "refetch") {
              setColumns([]);
              fetchCollection();
            }
            setSurveyAreaDrawerOpen(false);
          }}
          collectionId={collection.id}
          existingSurveyArea={collection?.survey_area}
          existingMatchedRegions={collection?.matched_regions}
        />
      )}
    </CollectionItemsTableWrapper>
  );
};

export default CollectionItemsTable;